import React, { ReactNode } from 'react';
import classNames from 'classnames/bind';
import { Text as RebassText, type TextProps as RebassTextProps } from 'rebass';
import { formatString } from 'strings';
import styles from './Text.module.css';

/**
 * Anything not destructured below is spread into Rebass's `Text`. These are the
 * pass-through props call sites actually use; they were missing from this type,
 * not from the component, so they are picked from Rebass's own props rather than
 * re-declared — styled-system types them more precisely than `string`.
 *
 * `fontSize`/`fontWeight`/`width` are deliberately *not* forwarded: they are
 * consumed here as CSS-module class names and inline styles.
 */
type TextProps = Pick<
  RebassTextProps,
  'as' | 'variant' | 'color' | 'textAlign' | 'ml' | 'mr'
> & {
  fontSize?: string;
  fontWeight?: string;
  className?: string;
  style?: Record<string, unknown>;
  tt?: string;
  ow?: string;
  width?: number;
  children?: ReactNode;
};

const cx = classNames.bind(styles);

const renderString = (node: ReactNode) => {
  if (typeof node === 'string') {
    return formatString(node);
  }
  return node;
};

export const Text = React.forwardRef<unknown, TextProps>(
  (
    {
      className,
      tt,
      ow,
      width,
      style,
      children,
      fontSize,
      fontWeight,
      ...props
    },
    ref,
  ) => {
    const textStyle: Record<string, unknown> = {
      ...style,
      textTransform: tt,
      overflowWrap: ow,
    };

    if (width) {
      textStyle.textOverflow = 'ellipsis';
      textStyle.overflow = 'hidden';
      textStyle.whiteSpace = 'pre';
      textStyle.width = width;
    }

    return (
      <RebassText
        ref={ref}
        {...props}
        style={textStyle}
        className={cx('text', fontSize, fontWeight, className)}
      >
        {renderString(children)}
      </RebassText>
    );
  },
);

Text.displayName = 'Text';

Text.defaultProps = {
  as: 'span',
};
